import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-unauthorized',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="unauthorized-container">
      <div class="unauthorized-card">
        <div class="icon-container">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
          </svg>
        </div>
        
        <h1>Access Denied</h1>
        <p class="message">{{ message }}</p>
        
        <div class="details" *ngIf="errorDetails">
          <p class="error-details">{{ errorDetails }}</p>
        </div>
        
        <div class="actions">
          <button class="btn-login" (click)="onLogin()">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
              <polyline points="10 17 15 12 10 7"></polyline>
              <line x1="15" y1="12" x2="3" y2="12"></line>
            </svg>
            Sign In
          </button>
          
          <button class="btn-secondary" (click)="onRetry()" *ngIf="showRetry">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10"></polyline>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
            </svg>
            Try Again
          </button>
        </div>
        
        <div class="help-text">
          <p>If you believe you should have access, please contact your administrator.</p>
        </div>
        
        <div class="logo">
          <span>DatCI - RTAs</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .unauthorized-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }

    .unauthorized-card {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(10px);
      border-radius: 16px;
      padding: 48px;
      text-align: center;
      max-width: 450px;
      width: 90%;
      border: 1px solid rgba(255, 255, 255, 0.1);
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    }

    .icon-container {
      margin-bottom: 24px;
      color: #e94560;
    }

    .icon-container svg {
      filter: drop-shadow(0 0 20px rgba(233, 69, 96, 0.5));
    }

    h1 {
      color: #ffffff;
      font-size: 28px;
      font-weight: 600;
      margin: 0 0 12px;
    }

    .message {
      color: rgba(255, 255, 255, 0.8);
      font-size: 16px;
      margin: 0 0 24px;
      line-height: 1.5;
    }

    .details {
      background: rgba(233, 69, 96, 0.1);
      border: 1px solid rgba(233, 69, 96, 0.3);
      border-radius: 8px;
      padding: 12px 16px;
      margin-bottom: 24px;
    }

    .error-details {
      color: #e94560;
      font-size: 13px;
      margin: 0;
      font-family: monospace;
    }

    .actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      margin-bottom: 32px;
    }

    .btn-login, .btn-secondary {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 14px 28px;
      border-radius: 8px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      border: none;
    }

    .btn-login {
      background: linear-gradient(135deg, #e94560 0%, #c73e54 100%);
      color: #ffffff;
    }

    .btn-login:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(233, 69, 96, 0.4);
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: #ffffff;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: translateY(-2px);
    }

    .help-text {
      margin-bottom: 24px;
    }

    .help-text p {
      color: rgba(255, 255, 255, 0.5);
      font-size: 13px;
      margin: 0;
    }

    .logo {
      padding-top: 24px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .logo span {
      color: rgba(255, 255, 255, 0.4);
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 1px;
    }

    :host-context(.dark) .unauthorized-container {
      background: linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 50%, #2d2d2d 100%);
    }
  `]
})
export class UnauthorizedComponent {
  @Input() message = 'You are not authorized to access this application.';
  @Input() errorDetails: string | null = null;
  @Input() showRetry = true;
  
  @Output() loginClick = new EventEmitter<void>();
  @Output() retryClick = new EventEmitter<void>();

  onLogin(): void {
    this.loginClick.emit();
  }

  onRetry(): void {
    this.retryClick.emit();
  }
}
